import {React, useRef} from 'react';
import {Box, IconButton, Typography, TextField, Tooltip, Alert} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx-js-style';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import {useTranslation} from "react-i18next";

const KeyValueDisplay = ({fieldPath, fieldText, valuePath, valueText, renderEditableField}) => (
    <Box display="flex" alignItems="center">
        <Box minWidth={140} flexShrink={0}>
            {renderEditableField(fieldPath, fieldText, true, true)}
        </Box>
        <Box flex={1}>
            {renderEditableField(valuePath, valueText, true)}
        </Box>
    </Box>
);


const EditableField = ({
                           panelWidth,
                           isEditing,
                           editedValue,
                           fieldLabel,
                           fieldKey,
                           bold,
                           inline,
                           onChange,
                           onConfirm,
                           onCancel,
                           onStartEdit,
                           originalValue,
                       }) => {
    const {t} = useTranslation();

    const didConfirmRef = useRef(false);
    const didCancelRef = useRef(false);

    const isEmpty = originalValue == null || String(originalValue).trim() === "";

    return (
        <Box
            display="flex"
            alignItems="center"
            sx={{
                position: 'relative',
                cursor: isEditing ? 'text' : 'pointer',
            }}
            gap={0.25}
            onDoubleClick={(e) => {
                e.stopPropagation();
                if (!isEditing) onStartEdit();
            }}
            onClick={(e) => {
                // convenience: single-click starts edit when empty
                if (isEmpty && !isEditing) {
                    e.stopPropagation();
                    onStartEdit();
                }
            }}
        >
            {isEditing ? (
                <TextField
                    size="small"
                    variant="standard"
                    autoFocus
                    value={editedValue ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            didConfirmRef.current = true;
                            onConfirm();
                            setTimeout(() => {
                                didConfirmRef.current = false;
                            }, 0);
                        } else if (e.key === 'Escape') {
                            didCancelRef.current = true;
                            onCancel();
                            setTimeout(() => {
                                didCancelRef.current = false;
                            }, 0);
                        }
                    }}
                    onBlur={() => {
                        if (!didConfirmRef.current && !didCancelRef.current) onConfirm();
                        didConfirmRef.current = false;
                        didCancelRef.current = false;
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{minWidth: inline ? 80 : '100%', width: '100%'}}
                />
            ) : (
                <Typography
                    variant="body2"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: bold ? 'bold' : 'normal',
                        ...(isEmpty ? {color: 'text.disabled'} : {}),
                        ...(panelWidth >= 520
                            ? {whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere'}
                            : panelWidth >= 360
                                ? {
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }
                                : {whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}),
                    }}
                    title={isEmpty ? t("editable.double_click_to_edit") : undefined}
                >
                    {fieldLabel ?
                        <strong>{fieldLabel}:</strong> : ''} {isEmpty ? t("editable.double_click_to_edit") : originalValue}
                </Typography>
            )}
        </Box>
    );
};

const AnnotationItem = ({annotation, renderEditableField, onDelete, onTablePatch, tableWarningText}) => {

    const {t} = useTranslation();
    if (!annotation) return null;
    const type = annotation.type;

    const exportTableToExcel = (a) => {
        // ——— Gather table data
        const label = (a?.label || "Table").trim();
        const headers = Array.isArray(a?.table_header) ? a.table_header : [];
        const rows = Array.isArray(a?.rows) ? a.rows : [];

        const width = Math.max(
            headers.length,
            rows.reduce((m, r) => Math.max(m, Array.isArray(r) ? r.length : 0), 0)
        );
        const w = Math.max(1, width);

        // ——— Build ws_data with the SAME 3-row header structure as ViewerContainer
        // Top row: table label (merged across all columns)
        const header0 = new Array(w).fill("");
        header0[0] = label || "Table";
        // Middle row: column headers (or auto names)
        const header1 = Array.from({length: w}, (_, i) => headers[i] ?? `Column ${i + 1}`);
        // Third header row: empty, and we will merge row 1..2 per column (like ungrouped branch)
        const header2 = new Array(w).fill("");

        const ws_data = [header0, header1, header2, ...rows.map(r => {
            const out = new Array(w).fill("");
            (r || []).forEach((v, i) => {
                if (i < w) out[i] = v ?? "";
            });
            return out;
        })];

        // ——— Create sheet and apply the SAME merges as ungrouped tables in ViewerContainer
        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        const merges = [];
        // Merge the top header (row 0) across all columns
        merges.push({s: {r: 0, c: 0}, e: {r: 0, c: w - 1}});
        // For each column, merge header row1..row2 (rows 1–2)
        for (let c = 0; c < w; c++) {
            merges.push({s: {r: 1, c}, e: {r: 2, c}});
        }
        ws["!merges"] = merges;

        // ——— Auto column widths (same approach)
        ws["!cols"] = ws_data[0].map((_, colIdx) => {
            const colTexts = ws_data.map(row => (row[colIdx] ?? "").toString());
            const maxLen = Math.max(...colTexts.map(v => v.length), 10);
            return {wch: Math.min(Math.max(10, maxLen + 2), 60)};
        });

        // ——— Styling: mirror centerBoldHeader + data borders from ViewerContainer
        const centerBoldHeader = {
            alignment: {horizontal: "center", vertical: "center"},
            font: {bold: true},
            border: {
                top: {style: "thin", color: {rgb: "000000"}},
                bottom: {style: "thin", color: {rgb: "000000"}},
                left: {style: "thin", color: {rgb: "000000"}},
                right: {style: "thin", color: {rgb: "000000"}},
            },
            fill: {fgColor: {rgb: "E0E0E0"}},
        };
        // Apply header style to rows 0,1,2
        for (let c = 0; c < w; c++) {
            for (let r = 0; r <= 2; r++) {
                const addr = XLSX.utils.encode_cell({r, c});
                if (ws[addr]) ws[addr].s = centerBoldHeader;
            }
        }

        // Data borders: thin black grid everywhere
        const dataStart = 3;
        const dataEnd = ws_data.length - 1;
        if (dataEnd >= dataStart) {
            for (let r = dataStart; r <= dataEnd; r++) {
                for (let c = 0; c < w; c++) {
                    const addr = XLSX.utils.encode_cell({r, c});
                    const border = {
                        top: {style: "thin", color: {rgb: "000000"}},
                        bottom: {style: "thin", color: {rgb: "000000"}},
                        left: {style: "thin", color: {rgb: "000000"}},
                        right: {style: "thin", color: {rgb: "000000"}},
                    };
                    const style = {alignment: {horizontal: "left", vertical: "center"}, border};
                    if (ws[addr]) ws[addr].s = style;
                }
            }
        }

        // ——— Save
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, (label || "Table").slice(0, 31) || "Table");
        const safeName = label.replace(/[\\/:*?"<>|\u0000-\u001F]/g, "_");
        XLSX.writeFile(wb, `${safeName || "table"}.xlsx`);
    };

    switch (type) {
        case 'key_value':
            return (
                <Box display="flex" alignItems="center" mb={1}>
                    {/* Key column */}
                    <Box minWidth={160} flexShrink={0}>
                        {renderEditableField("field", annotation.field.text, true, true)}
                    </Box>
                    {/* Value column */}
                    <Box flex={1}>
                        {renderEditableField("value", annotation.value.text, true)}
                    </Box>
                    {/*                    {onDelete && (
                        <IconButton size="small" onClick={onDelete}>
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    )}*/}
                </Box>
            );

        case 'entity':
            return (
                <Box display="flex" alignItems="center" mb={1}>
                    <Box minWidth={160} flexShrink={0}>
                        {renderEditableField("field", annotation.entity_type, true, true)}
                    </Box>
                    <Box flex={1}>
                        {renderEditableField("value", annotation.text, true)}
                    </Box>
                    {/*                    {onDelete && (
                        <IconButton size="small" onClick={onDelete}>
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    )}*/}
                </Box>
            );


        case 'summary_row':
            return (
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        {renderEditableField("label", annotation.label, true, true)}
                        {renderEditableField("text", annotation.text, true)}
                    </Box>
                    {/*                    {onDelete && (
                        <IconButton size="small" onClick={onDelete}>
                            <DeleteIcon fontSize="small"/>
                        </IconButton>
                    )}*/}
                </Box>
            );


        case 'table': {
            const TableEditor = require("./TableEditor").default;
            const editor = renderEditableField("label", annotation.label ?? "Table", false, true);

            return (
                <Box display="flex" flexDirection="column" gap={1} mb={1} sx={{position: 'relative'}}>
                    {tableWarningText && (
                        <Alert severity="warning" variant="outlined" sx={{mb: 1}}>
                            {tableWarningText}
                        </Alert>
                    )}
                    {/* Title row + download button */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        {editor}
                        <Tooltip title={t("table.download_excel")}>
                            <IconButton
                                size="small"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    exportTableToExcel(annotation);
                                }}
                                sx={{ml: 1}}
                                aria-label={t("table.download_aria")}
                            >
                                <DownloadIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <TableEditor
                        table={{
                            label: annotation.label ?? "Table",
                            table_header: Array.isArray(annotation.table_header) ? annotation.table_header : [],
                            rows: Array.isArray(annotation.rows) ? annotation.rows : [],
                        }}
                        renderCell={(fk, fv, inline = true, bold = false) => renderEditableField(fk, fv, inline, bold)}
                        onChange={(patch) => onTablePatch?.(patch)}
                        showToolbar={false}
                    />
                </Box>
            );
        }

        case 'key_value_group': {
            return (
                <Box display="flex" flexDirection="column" gap={1}>
                    {/* Group label remains editable */}
                    {renderEditableField("group_label", annotation.group_label, false, true)}

                    {/* Map over the sub-items */}
                    {annotation.items.map((subItem, index) => (
                        <Box
                            key={index}
                            mt={1}
                            p={1}
                            border="1px solid"
                            borderColor="divider"
                            borderRadius={1}
                            bgcolor="background.paper"
                            sx={{'&:hover': {bgcolor: 'action.hover'}}}
                        >
                            <KeyValueDisplay
                                fieldPath={`items.${index}.field`}
                                fieldText={subItem.field}
                                valuePath={`items.${index}.value.text`}
                                valueText={subItem.value.text}
                                renderEditableField={renderEditableField}
                            />
                        </Box>
                    ))}
                </Box>
            );
        }


        default:
            return (
                <Box display="flex" alignItems="center" gap={1}>
                    {annotation.status === 'loading' ? (
                        <Box display="flex" justifyContent="center" alignItems="center" width="100%" height={40}>
                            <CircularProgress size={20}/>
                        </Box>
                    ) : annotation.status === 'error' ? (
                        <Typography color="error">Extraction failed</Typography>
                    ) : (
                        <Typography color="textSecondary">Unknown annotation type.</Typography>
                    )}
                </Box>
            );
    }
};

export default AnnotationItem;
export {EditableField};